import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { tap, timer } from 'rxjs';

import { useDrop } from '@/hook';
import type { VFileBrowserRef } from '@/package';
import {
  DriveFolderUpload,
  File,
  VFileBrowser,
  VFileBrowserEmptyContentList,
  VFileBrowserEmptyTree,
  VFlex,
  VStack,
  useSubscriptions,
} from '@/package';
import { useCollectionContext } from '@/provider';
import { useCollectionManagementStore } from '@/store';
import type { S3ListItem } from '@/type';

import { LoadingIndicator } from '../LoadingIndicator';

interface CollectionPageMainProps {
  fileBrowserRef?: React.RefObject<VFileBrowserRef | null>;
}

const CollectionPageMain = ({ fileBrowserRef }: CollectionPageMainProps) => {
  const {
    currentDirectoryUuid,
    itemByUuid,
    itemList,

    moveRoot,
    readDirectory: _readDirectory,
    select,
    removeDirectory,
    removeFile,
    setCurrentDirectoryUuid,
    uploadDirectory,
    uploadFileList,
  } = useCollectionManagementStore();
  const { isInitializedLoading, openRemoveConfirm, setSelectedItemList } =
    useCollectionContext();
  const { t } = useTranslation();
  const [isContentLoading, setIsContentLoading] = useState(false);

  const $el = useRef<HTMLDivElement>(null);
  const $fileBrowserContentList = useRef<HTMLDivElement>(null);

  const openFileDialog = (
    type: 'directory' | 'file',
    callback: (fileList: Nullish<FileList>) => void,
  ) => {
    const input = document.createElement('input');

    input.type = 'file';
    input.multiple = true;
    input.webkitdirectory = type === 'directory';
    input.onchange = () => callback(input.files);
    input.click();
  };

  const readDirectory = (
    param: Parameters<typeof _readDirectory>[0],
    enabledLoading = false,
  ) => {
    _readDirectory(param).finally(() => {
      setIsContentLoading(
        useCollectionManagementStore.getState().getCurrentDir().isLoading ??
          false,
      );
    });

    if (enabledLoading)
      setIsContentLoading(
        useCollectionManagementStore.getState().getCurrentDir().isLoading ??
          true,
      );
  };

  const onUploadButtonClick = (type: 'directory' | 'file') => {
    openFileDialog(type, fileList => {
      if (fileList == null) return;

      uploadFileList(fileList);
    });
  };

  const dropController = useDrop(async event => {
    const itemList = event.dataTransfer?.items;

    if (!itemList) return;

    try {
      await uploadDirectory(itemList);
    } catch (error) {
      console.warn(error);
    } finally {
      readDirectory({
        childrenOption: {
          autoAdd: true,
        },
      });
    }
  });

  useSubscriptions(() => {
    dropController.disconnect();

    if (!itemList.length) return [];

    return [
      timer(0)
        .pipe(
          tap(() => {
            $fileBrowserContentList.current =
              $el.current!.querySelector<HTMLDivElement>(
                '[data-testid="v-virtual-scroll-container"]',
              )!;

            fileBrowserRef?.current?.setCurrentDirUuid(currentDirectoryUuid);
            dropController.connect($fileBrowserContentList.current);
          }),
        )
        .subscribe(),
    ];
  }, [itemList]);

  return (
    <>
      <VStack
        ref={$el}
        className="relative z-10 -mx-2 h-full w-[calc(100%+var(--spacing-2))] flex-1 overflow-hidden pt-2"
      >
        {isInitializedLoading ? (
          <LoadingIndicator className="relative mx-auto mt-scale/64 h-fit w-fit bg-transparent" />
        ) : (
          <VFileBrowser
            ref={fileBrowserRef}
            itemList={itemList}
            emptyRenderer={{
              contentList: () => (
                <VFileBrowserEmptyContentList
                  message={t('storage.emptyContentList.gastrohypertonic')}
                  renderDropdownItem={({ item }) => (
                    <VFlex className="text-body-lg" items="center" gap={0.25}>
                      {item.value.match('folder') ? (
                        <DriveFolderUpload className="size-5" />
                      ) : (
                        <File />
                      )}
                      {item.label}
                    </VFlex>
                  )}
                  onUploadButtonClick={onUploadButtonClick}
                />
              ),
              tree: () => (
                <VFileBrowserEmptyTree
                  message={t('storage.emptyTree.movement')}
                />
              ),
            }}
            loadingContent={isContentLoading}
            loadingContentRenderer={() => (
              <LoadingIndicator className="relative mx-auto mt-scale/64 h-fit w-fit bg-transparent" />
            )}
            onItemDblClick={event => {
              if (event.target.item.isDir) {
                const uuid = event.target.item.uuid;
                const dir = itemByUuid[uuid];
                const isRoot = dir == null;

                if (isRoot) {
                  moveRoot();
                } else {
                  setCurrentDirectoryUuid(uuid);
                  readDirectory(
                    {
                      path: dir.key,
                      childrenOption: { autoAdd: true },
                    },
                    true,
                  );
                }
              } else {
                event.preventDefault();
              }
            }}
            onItemRemove={event => {
              const {
                target: { itemList },
              } = event;

              setSelectedItemList(() => [...itemList]);
              openRemoveConfirm(() => {
                Promise.all(
                  (itemList as S3ListItem[]).map(item =>
                    item.isDir
                      ? removeDirectory({ key: item.key })
                      : removeFile({ key: item.key }),
                  ),
                );
              });
            }}
            onEndOfDirectory={event => {
              const {
                target: {
                  item: { uuid },
                },
              } = event;
              const dir = itemByUuid[uuid];
              const startAfter = dir.lastStartAfter;

              if (!startAfter) return;

              readDirectory({
                startAfter,
                childrenOption: {
                  autoAdd: true,
                  appended: true,
                },
              });
            }}
            onItemSelect={event => {
              const itemList = event.target.itemList as S3ListItem[];

              select(itemList);
              setSelectedItemList(itemList);
            }}
            onItemClick={event => {
              if (event.target.src === 'tree') {
                setCurrentDirectoryUuid(event.target.item.uuid);

                readDirectory(
                  {
                    path: event.target.item.key,
                    childrenOption: {
                      autoAdd: true,
                    },
                  },
                  true,
                );
              } else {
                select([event.target.item as S3ListItem]);
              }
            }}
            onOpenDirectory={event => {
              const dir = itemByUuid[event.target.item.uuid!];

              readDirectory({
                path: dir.key,
                childrenOption: {
                  autoAdd: true,
                },
              });
            }}
          />
        )}
      </VStack>
    </>
  );
};

export default CollectionPageMain;
